import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { X, Save, AlertCircle, BookOpen, RefreshCw } from 'lucide-react'
import { renderApi } from '../../services/api'
import { useEditorStore } from '../../stores/editorStore'
import toast from 'react-hot-toast'
import CodeMirror from '@uiw/react-codemirror'
import { json as jsonLang, jsonParseLinter } from '@codemirror/lang-json'
import { EditorView } from '@codemirror/view'
import { linter, lintGutter } from '@codemirror/lint'

interface JsonEditorModalProps {
  isOpen: boolean
  onClose: () => void
}

export const JsonEditorModal: React.FC<JsonEditorModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const [jsonContent, setJsonContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [schemaErrors, setSchemaErrors] = useState<string[]>([])
  const [hasValidated, setHasValidated] = useState(false)
  const [showSyncWakeModal, setShowSyncWakeModal] = useState(false)
  const [showSyncSettingsModal, setShowSyncSettingsModal] = useState(false)
  const [isSyncWakeLoading, setIsSyncWakeLoading] = useState(false)
  const [isSyncSettingsLoading, setIsSyncSettingsLoading] = useState(false)
  const bumpPreviewVersion = useEditorStore((state) => state.bumpPreviewVersion)

  // Carregar o layout quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      loadLayout()
    } else {
      // Reset ao fechar
      setJsonContent('')
      setError(null)
      setSchemaErrors([])
      setHasValidated(false)
      setShowSyncWakeModal(false)
      setShowSyncSettingsModal(false)
      setIsSyncWakeLoading(false)
      setIsSyncSettingsLoading(false)
    }
  }, [isOpen])

  const loadLayout = async () => {
    setIsLoading(true)
    setError(null)
    setHasValidated(false)
    setSchemaErrors([])
    try {
      const response = await renderApi.getDevLayout()
      if (response.success && response.layout) {
        const root: any = { layout: response.layout }
        if (Array.isArray((response as any).components_registry)) {
          root.components_registry = (response as any).components_registry
        }
        const formatted = JSON.stringify(root, null, 2)
        setJsonContent(formatted)
      } else {
        setError('Não foi possível carregar o layout')
      }
    } catch (err: any) {
      console.error('Erro ao carregar layout:', err)
      setError(err?.response?.data?.error || 'Erro ao carregar layout')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    // Validar JSON
    let parsedJson: any
    try {
      parsedJson = JSON.parse(jsonContent)
    } catch (err) {
      setError('JSON inválido. Verifique a sintaxe.')
      return
    }

    // Esperamos um objeto raiz { layout: { ... } }
    if (!parsedJson || typeof parsedJson !== 'object' || !parsedJson.layout) {
      setError('JSON raiz deve ser um objeto com a chave "layout".')
      return
    }

    const layout = parsedJson.layout
    const componentsRegistry = Array.isArray(parsedJson.components_registry)
      ? parsedJson.components_registry
      : undefined

    // Só permite salvar se passou pela validação de schema
    if (!hasValidated || schemaErrors.length > 0) {
      setError('Antes de salvar, clique em "Verificar JSON" e corrija todos os erros.')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const response = await renderApi.saveDevLayout(layout, componentsRegistry)
      toast.success('Layout salvo e aplicado com sucesso!')
      
      // Atualizar o preview sem recarregar a página
      const responseWithTimestamp = response as { success: boolean; message: string; timestamp?: string }
      if (responseWithTimestamp.timestamp) {
        const timestamp = Date.parse(responseWithTimestamp.timestamp)
        useEditorStore.setState({ previewVersion: timestamp })
      } else {
        bumpPreviewVersion()
      }
      
      // Força recarregamento do propertiesPanel para refletir as alterações inseridas manualmente
      useEditorStore.setState({ hasLoadedComponents: false })
      useEditorStore.getState().loadComponents()
      useEditorStore.getState().loadRegistry()
      
      onClose()
    } catch (err: any) {
      console.error('Erro ao salvar layout:', err)
      setError(err?.response?.data?.error || 'Erro ao salvar layout')
    } finally {
      setIsSaving(false)
    }
  }

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonContent)
      const formatted = JSON.stringify(parsed, null, 2)
      setJsonContent(formatted)
      setError(null)
    } catch (err) {
      setError('JSON inválido. Não é possível formatar.')
    }
  }

  /**
   * Validação estrutural do layout, baseada em WAKE_JSON_COMPONENTS.md:
   * - layout.{page}.sections é um array
   * - cada seção tem name, component, label (editável) e values compatíveis
   * - props/description pertencem ao components_registry (não à seção)
   * - values não deve ter chaves desconhecidas em relação aos props do registry (quando disponível)
   * - arrays devem ser arrays, etc.
   */
  const validateLayoutSchema = (parsed: any): string[] => {
    const issues: string[] = []

    if (!parsed || typeof parsed !== 'object') {
      issues.push('JSON raiz deve ser um objeto.')
      return issues
    }

    if (!parsed.layout || typeof parsed.layout !== 'object') {
      issues.push('Campo "layout" é obrigatório e deve ser um objeto.')
      return issues
    }

    const allowedExtraValueKeys = ['class', 'style', 'styles']
    // "arrayItemID" (e "id" legado) são metadados técnicos usados pelo editor em itens de array (ordenação/dnd).
    // O id verdadeiro como chave deve estar declarado no schema.
    const allowedArrayItemExtraKeys = [...allowedExtraValueKeys, 'arrayItemID', 'id']
    const registry = Array.isArray(parsed.components_registry) ? parsed.components_registry : []
    const registryByComponent = new Map<string, any>()
    registry.forEach((entry: any) => {
      if (entry && typeof entry === 'object' && typeof entry.component === 'string') {
        registryByComponent.set(entry.component, entry)
      }
    })

    if (parsed.components_registry !== undefined) {
      if (!Array.isArray(parsed.components_registry)) {
        issues.push('"components_registry" deve ser um array quando informado.')
      } else {
        parsed.components_registry.forEach((entry: any, index: number) => {
          const entryPath = `components_registry[${index}]`
          if (!entry || typeof entry !== 'object') {
            issues.push(`${entryPath} deve ser um objeto.`)
            return
          }

          if (!entry.component || typeof entry.component !== 'string') {
            issues.push(`${entryPath}.component é obrigatório e deve ser string.`)
          }

          const entryId =
            typeof entry.name_id === 'string' && entry.name_id.trim()
              ? entry.name_id
              : typeof entry.name === 'string' && entry.name.trim()
                ? entry.name
                : null
          if (!entryId) {
            issues.push(`${entryPath}.name_id é obrigatório (aceita fallback legado em "name").`)
          }

          if (!entry.description || typeof entry.description !== 'string') {
            issues.push(`${entryPath}.description é obrigatório e deve ser string.`)
          }

          if (!entry.props || typeof entry.props !== 'object') {
            issues.push(`${entryPath}.props é obrigatório e deve ser um objeto.`)
          }
        })
      }
    }

    // Helpers para validar estruturas de valores mais complexos (image, button etc.)
    const validateImageValueAtPath = (valueObj: any, valuePath: string, pushIssue: (msg: string) => void) => {
      if (!valueObj || typeof valueObj !== 'object') {
        pushIssue(`${valuePath} deveria ser um objeto para o tipo "image".`)
        return
      }

      // Validar chaves conhecidas de image: desktop, mobile, link, alt_text
      const allowedImageKeys = ['desktop', 'mobile', 'link', 'alt_text', ...allowedExtraValueKeys]
      for (const k of Object.keys(valueObj)) {
        if (!allowedImageKeys.includes(k)) {
          pushIssue(
            `${valuePath}.${k} não é uma chave reconhecida para o tipo "image". Use desktop, mobile, link ou alt_text.`,
          )
        }
      }

      // Validar subobjetos desktop/mobile/link/alt_text
      const validateImageSubAtPath = (
        sub: any,
        subPath: string,
        kind: 'desktop' | 'mobile' | 'link' | 'alt_text',
      ) => {
        const isDesktopOrMobile = kind === 'desktop' || kind === 'mobile'

        if (sub === undefined) return
        if (!sub || typeof sub !== 'object') {
          pushIssue(`${subPath} deve ser um objeto com ao menos "value".`)
          return
        }
        const allowedSubKeys = isDesktopOrMobile
          ? ['value', 'type', ...allowedExtraValueKeys]
          : ['value', ...allowedExtraValueKeys]
        for (const k of Object.keys(sub)) {
          if (!allowedSubKeys.includes(k)) {
            pushIssue(
              `${subPath}.${k} não é uma chave reconhecida (use "value"${
                isDesktopOrMobile ? ' e opcionalmente "type"' : ''
              }).`,
            )
          }
        }
        if (typeof (sub as any).value === 'undefined') {
          pushIssue(`${subPath}.value é obrigatório para imagens.`)
        }
        if (isDesktopOrMobile && typeof (sub as any).type !== 'undefined') {
          const t = (sub as any).type
          if (t !== 'image' && t !== 'video') {
            pushIssue(`${subPath}.type deve ser "image" ou "video" se definido.`)
          }
        }
      }

      validateImageSubAtPath((valueObj as any).desktop, `${valuePath}.desktop`, 'desktop')
      validateImageSubAtPath((valueObj as any).mobile, `${valuePath}.mobile`, 'mobile')
      validateImageSubAtPath((valueObj as any).link, `${valuePath}.link`, 'link')
      validateImageSubAtPath((valueObj as any).alt_text, `${valuePath}.alt_text`, 'alt_text')
    }

    const validateButtonValueAtPath = (valueObj: any, valuePath: string, pushIssue: (msg: string) => void) => {
      if (!valueObj || typeof valueObj !== 'object') {
        pushIssue(`${valuePath} deveria ser um objeto para o tipo "button".`)
        return
      }

      // Button: normalmente tem subcampos text, link (objetos) e possivelmente class/style
      const allowedButtonKeys = ['text', 'link', ...allowedExtraValueKeys]
      for (const k of Object.keys(valueObj)) {
        if (!allowedButtonKeys.includes(k)) {
          pushIssue(`${valuePath}.${k} não é uma chave reconhecida para o tipo "button". Use text e link.`)
        }
      }

      const validateButtonSubAtPath = (sub: any, subPath: string, required: boolean) => {
        if (sub === undefined || sub === null) {
          if (required) {
            pushIssue(`${subPath} é obrigatório para campos do tipo "button".`)
          }
          return
        }
        if (!sub || typeof sub !== 'object') {
          pushIssue(`${subPath} deve ser um objeto com chave "value".`)
          return
        }
        const allowedButtonSubKeys = ['value', ...allowedExtraValueKeys]
        for (const k of Object.keys(sub)) {
          if (!allowedButtonSubKeys.includes(k)) {
            pushIssue(`${subPath}.${k} não é uma chave reconhecida (use "value").`)
          }
        }
        if (typeof (sub as any).value === 'undefined') {
          pushIssue(`${subPath}.value é obrigatório para campos do botão.`)
        }
      }

      validateButtonSubAtPath((valueObj as any).text, `${valuePath}.text`, true)
      validateButtonSubAtPath((valueObj as any).link, `${valuePath}.link`, true)
    }

    for (const [pageName, pageConfig] of Object.entries(parsed.layout as Record<string, any>)) {
      if (!pageConfig || typeof pageConfig !== 'object') {
        issues.push(`Página "${pageName}" deve ser um objeto.`)
        continue
      }

      // Nomes de seção devem ser únicos por página
      const sectionNames = new Set<string>()

      const sections = (pageConfig as any).sections
      if (!Array.isArray(sections)) {
        issues.push(`Página "${pageName}" deve ter um array "sections".`)
        continue
      }

      sections.forEach((section: any, index: number) => {
        const sectionPath = `layout.${pageName}.sections[${index}]`
        const resolvedSectionId =
          section && typeof section === 'object'
            ? typeof section.name_id === 'string' && section.name_id.trim()
              ? section.name_id
              : typeof section.name === 'string' && section.name.trim()
                ? section.name
                : null
            : null
        const sectionNameValue = resolvedSectionId
          ? resolvedSectionId
          : `#${index + 1}`
        const pushIssue = (msg: string) => {
          issues.push(`Seção "${sectionNameValue}" (${sectionPath}): ${msg}`)
        }

        if (!section || typeof section !== 'object') {
          pushIssue('deve ser um objeto.')
          return
        }

        if (!resolvedSectionId) {
          pushIssue('campo "name_id" é obrigatório (aceita fallback legado em "name").')
        } else {
          if (sectionNames.has(resolvedSectionId)) {
            pushIssue(
              `possui identificador duplicado ("${resolvedSectionId}"). Cada seção da página deve ter um identificador único.`,
            )
          } else {
            sectionNames.add(resolvedSectionId)
          }
        }
        if (!section.component || typeof section.component !== 'string') {
          pushIssue('campo "component" é obrigatório e deve ser string.')
        }

        if (!section.label || typeof section.label !== 'string') {
          pushIssue('campo "label" é obrigatório e deve ser string.')
        }

        const registryDef =
          section.component && typeof section.component === 'string'
            ? registryByComponent.get(section.component)
            : undefined
        const props =
          registryDef && typeof registryDef.props === 'object'
            ? registryDef.props
            : section.props && typeof section.props === 'object'
              ? section.props
              : undefined

        const allowedFieldTypes = [
          'text',
          'image',
          'color',
          'link',
          'class',
          'object',
          'array',
          'query',
          'id',
          'select',
          'button',
          'date',
        ]
        const allowedFieldConfigKeys = ['type', 'label', 'description', 'props', 'items', 'options', 'value']
        const MAX_DEPTH = 10

        if (props && typeof props === 'object') {
          const propKeys = Object.keys(props)
          if (propKeys.length === 0) {
            pushIssue('props do schema do componente deve ter ao menos um campo configurado.')
          }

          const validateFieldConfig = (cfg: any, cfgPath: string, depth: number) => {
            if (depth > MAX_DEPTH) {
              pushIssue(`${cfgPath}: profundidade máxima de aninhamento (${MAX_DEPTH}) excedida.`)
              return
            }
            if (!cfg || typeof cfg !== 'object') {
              pushIssue(`${cfgPath} deve ser um objeto FieldConfig válido.`)
              return
            }

            const fType = cfg.type
            if (!fType || typeof fType !== 'string') {
              pushIssue(`${cfgPath}.type é obrigatório e deve ser string.`)
            } else if (!allowedFieldTypes.includes(fType)) {
              pushIssue(
                `${cfgPath}.type ("${fType}") não é um tipo suportado. Use um de: ${allowedFieldTypes.join(', ')}.`,
              )
            }

            if (!cfg.label || typeof cfg.label !== 'string') {
              pushIssue(`${cfgPath}.label é obrigatório e deve ser string.`)
            }

            if (!cfg.description || typeof cfg.description !== 'string') {
              pushIssue(`${cfgPath}.description é obrigatório e deve ser string.`)
            }

            for (const key of Object.keys(cfg)) {
              if (!allowedFieldConfigKeys.includes(key)) {
                pushIssue(
                  `${cfgPath}.${key} não é uma chave reconhecida em FieldConfig. Verifique a ortografia ou as docs de schema.`,
                )
              }
            }

            if (fType === 'array' || fType === 'object') {
              if (!cfg.props || typeof cfg.props !== 'object') {
                pushIssue(`${cfgPath}.props é obrigatório e deve ser um objeto para campos do tipo "${fType}".`)
              } else {
                for (const [innerKey, innerCfg] of Object.entries(cfg.props as Record<string, any>)) {
                  validateFieldConfig(innerCfg, `${cfgPath}.props["${innerKey}"]`, depth + 1)
                }
              }
            }
          }

          for (const [propKey, fieldCfg] of Object.entries(props as Record<string, any>)) {
            validateFieldConfig(fieldCfg, `props["${propKey}"]`, 0)
          }
        }

        const values = section.values
        if (!values || typeof values !== 'object') {
          pushIssue('"values" é obrigatório e deve ser um objeto com os valores iniciais.')
          return
        }

        if (values && typeof values === 'object') {
          if (props && typeof props === 'object') {
            const validateValueAgainstSchema = (
              valueObj: any,
              fieldConfig: any,
              valuePath: string,
              depth: number,
            ) => {
              if (depth > MAX_DEPTH) return
              if (!fieldConfig || typeof fieldConfig !== 'object') return
              const fieldType = fieldConfig.type

              if (fieldType === 'array') {
                if (!Array.isArray(valueObj)) {
                  pushIssue(`${valuePath} deve ser um array (conforme type: "array" em props).`)
                } else if (!fieldConfig.props || typeof fieldConfig.props !== 'object') {
                  pushIssue(`${valuePath}: props do schema é obrigatório para campos do tipo "array".`)
                } else {
                  valueObj.forEach((item: any, itemIndex: number) => {
                    const itemPath = `${valuePath}[${itemIndex}]`
                    if (!item || typeof item !== 'object') {
                      pushIssue(`${itemPath} deve ser um objeto, conforme schema de props.`)
                      return
                    }
                    for (const itemKey of Object.keys(item)) {
                      if (!fieldConfig.props[itemKey] && !allowedArrayItemExtraKeys.includes(itemKey)) {
                        pushIssue(`${itemPath}.${itemKey} não está declarado em props.`)
                      }
                    }

                    if (item.arrayItemID !== undefined) {
                      if (typeof item.arrayItemID !== 'string' && typeof item.arrayItemID !== 'number') {
                        pushIssue(`${itemPath}.arrayItemID deve ser uma string ou número.`)
                      }
                    } else if (item.id !== undefined && !fieldConfig.props.id) {
                      if (typeof item.id !== 'string' && typeof item.id !== 'number') {
                        pushIssue(`${itemPath}.id (legado) deve ser uma string ou número. Recomendado usar arrayItemID.`)
                      }
                    }

                    for (const [itemKey, itemVal] of Object.entries(item as Record<string, any>)) {
                      const innerCfg = fieldConfig.props[itemKey]
                      if (!innerCfg || typeof innerCfg !== 'object') continue
                      validateValueAgainstSchema(itemVal, innerCfg, `${itemPath}.${itemKey}`, depth + 1)
                    }
                  })
                }
                return
              }

              if (fieldType === 'object') {
                if (valueObj && typeof valueObj === 'object' && !Array.isArray(valueObj)) {
                  if (fieldConfig.props && typeof fieldConfig.props === 'object') {
                    for (const [childKey, childVal] of Object.entries(valueObj as Record<string, any>)) {
                      if (!fieldConfig.props[childKey] && !allowedExtraValueKeys.includes(childKey)) {
                        pushIssue(`${valuePath}.${childKey} não está declarado em props do object.`)
                      }
                      const childCfg = fieldConfig.props[childKey]
                      if (childCfg && typeof childCfg === 'object') {
                        validateValueAgainstSchema(childVal, childCfg, `${valuePath}.${childKey}`, depth + 1)
                      }
                    }
                  }
                }
                return
              }

              if (fieldType === 'text' || fieldType === 'id' || fieldType === 'select' || fieldType === 'date') {
                if (!valueObj || typeof valueObj !== 'object' || typeof valueObj.value === 'undefined') {
                  pushIssue(`${valuePath} deveria ter uma chave "value" para o tipo "${fieldType}".`)
                }
                if (fieldType === 'select' && Array.isArray(fieldConfig.options)) {
                  const allowed = (fieldConfig.options as Array<{ value: string }>).map((o) => o.value)
                  const v = valueObj?.value
                  if (typeof v !== 'undefined' && !allowed.includes(v)) {
                    pushIssue(
                      `${valuePath}.value ("${v}") não está entre as opções permitidas: [${allowed.join(', ')}].`,
                    )
                  }
                }
                return
              }

              if (fieldType === 'image') {
                validateImageValueAtPath(valueObj, valuePath, pushIssue)
                return
              }

              if (fieldType === 'button') {
                validateButtonValueAtPath(valueObj, valuePath, pushIssue)
                return
              }
            }

            for (const [valueKey, valueObj] of Object.entries(values as Record<string, any>)) {
              if (!props[valueKey] && !allowedExtraValueKeys.includes(valueKey)) {
                pushIssue(
                  `values["${valueKey}"] não tem definição correspondente em props. Verifique se o campo foi declarado em props.`,
                )
              }

              const fieldConfig = props[valueKey]
              if (fieldConfig && typeof fieldConfig === 'object') {
                validateValueAgainstSchema(valueObj, fieldConfig, `values["${valueKey}"]`, 0)
              }
            }
          }
        }
      })
    }

    return issues
  }

  const handleValidateSchema = () => {
    try {
      const parsed = JSON.parse(jsonContent)
      const issues = validateLayoutSchema(parsed)
      setSchemaErrors(issues)
      setHasValidated(true)

      if (issues.length === 0) {
        toast.success('JSON válido de acordo com o schema base.')
        setError(null)
      } else {
        toast.error('Foram encontrados problemas no JSON. Veja a lista de erros abaixo.')
      }
    } catch (err) {
      setSchemaErrors(['JSON inválido. Não foi possível analisar a estrutura.'])
      setHasValidated(false)
      setError('JSON inválido. Verifique a sintaxe antes de validar o schema.')
    }
  }

  const handleSyncWake = async () => {
    setShowSyncWakeModal(false)
    setIsSyncWakeLoading(true)
    setError(null)
    try {
      const response = await renderApi.getWakeJson()
      const wakeResponse = response as {
        success: boolean
        layout: any
        components_registry?: any[]
        message?: string
      }
      if (response.success && response.layout) {
        const root: any = { layout: response.layout }
        if (Array.isArray(response.components_registry)) {
          root.components_registry = response.components_registry
        }
        const formatted = JSON.stringify(root, null, 2)
        setJsonContent(formatted)
        setHasValidated(false)
        setSchemaErrors([])
        toast.success(wakeResponse.message || 'Dados baixados da Wake e exibidos no editor. (Não salvos ainda)')
      } else {
        throw new Error('Formato inválido recebido da Wake API')
      }
    } catch (err: any) {
      console.error('Erro ao buscar dados da Wake:', err)
      const message = err?.response?.data?.error || 'Erro ao buscar dados da Wake'
      setError(message)
      toast.error(message)
    } finally {
      setIsSyncWakeLoading(false)
    }
  }

  const handleSyncSettings = async () => {
    setShowSyncSettingsModal(false)
    setIsSyncSettingsLoading(true)
    setError(null)
    try {
      const response = await renderApi.getDevSettings()
      if (response.success && response.layout) {
        const root: any = { layout: response.layout }
        if (Array.isArray(response.components_registry)) {
          root.components_registry = response.components_registry
        }
        const formatted = JSON.stringify(root, null, 2)
        setJsonContent(formatted)
        setHasValidated(false)
        setSchemaErrors([])
        toast.success('settings.json do repositório carregado no editor.')
      } else {
        setError('Não foi possível carregar o settings.json')
        toast.error('Não foi possível carregar o settings.json')
      }
    } catch (err: any) {
      console.error('Erro ao buscar settings.json:', err)
      const message = err?.response?.data?.error || 'Erro ao buscar settings.json'
      setError(message)
      toast.error(message)
    } finally {
      setIsSyncSettingsLoading(false)
    }
  }

  const isBusy = isLoading || isSaving || isSyncWakeLoading || isSyncSettingsLoading

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className="modal-backdrop flex items-center justify-center z-[9999]">
      <div className="glass-modal modal-enter rounded-xl max-w-5xl w-full mx-4 max-h-[90vh] h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-lg font-mono">{"{}"}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Editor de Layout JSON
              </h3>
              <p className="text-sm text-gray-600">
                Edite o layout do settings.json (dev mode)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isBusy}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando layout...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 flex-1">{error}</p>
                </div>
              )}

              {/* JSON Editor */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Layout JSON
                  </label>
                  <button
                    onClick={handleFormat}
                    className="text-xs px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isBusy}
                  >
                    Formatar JSON
                  </button>
                </div>
                <div className="flex-1 min-h-0 border border-gray-300 rounded-md overflow-auto">
                  <CodeMirror
                  value={jsonContent}
                    height="100%"
                    theme={EditorView.theme(
                      {
                        '&': {
                          backgroundColor: 'white',
                          fontSize: '0.875rem',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        },
                        '.cm-content': {
                          padding: '0.75rem',
                        },
                      },
                      { dark: false },
                    )}
                    extensions={[
                      jsonLang(),
                      linter(jsonParseLinter()),
                      lintGutter(),
                      EditorView.lineWrapping,
                    ]}
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLine: true,
                      highlightActiveLineGutter: true,
                    }}
                    onChange={(value) => {
                      setJsonContent(value)
                      // Deixar a indicação de erro visual a cargo do linter;
                      // mensagem textual será validada apenas no salvar.
                      setError(null)
                      setHasValidated(false)
                      setSchemaErrors([])
                    }}
                  placeholder='{"layout": {"home": {"sections": []}}}'
                    readOnly={isSaving}
                />
                </div>
              </div>

              {/* Lista de erros de schema (detalhada) */}
              {schemaErrors.length > 0 && (
                <div className="mt-3 max-h-32 overflow-auto rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
                  <p className="text-xs font-medium text-yellow-800 mb-1">
                    Problemas encontrados no JSON:
                  </p>
                  <ul className="list-disc pl-4 space-y-0.5 text-xs text-yellow-900">
                    {schemaErrors.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Resumo da validação (sempre abaixo da lista de problemas) */}
              {hasValidated && (
                <div className="mt-2 text-[11px] text-gray-500">
                  {schemaErrors.length === 0
                    ? 'Sem problemas estruturais encontrados.'
                    : `${schemaErrors.length} problema(s) encontrado(s).`}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="flex items-center space-x-3">
            <a
              href="https://wicomm-com-br.gitbook.io/site-editor"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium border border-pink-500 text-pink-600 bg-white rounded-md hover:bg-pink-50 transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Ver documentação</span>
            </a>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowSyncWakeModal(true)}
                disabled={isBusy}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Sync Wake</span>
              </button>
              <button
                onClick={() => setShowSyncSettingsModal(true)}
                disabled={isBusy}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Sync settings.json</span>
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleValidateSchema}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              disabled={isBusy}
            >
              Verificar JSON
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isBusy}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isBusy || !hasValidated || schemaErrors.length > 0}
              className="px-4 py-2 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Salvar e Aplicar</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmação - Sync Wake */}
      {showSyncWakeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-md max-w-md w-full mx-4 p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar Sync Wake</h3>
            <p className="text-sm text-gray-700 mb-4">
              Isso vai buscar o conteúdo atualizado da API da Wake (componente configurado) e exibí-lo no editor de JSON abaixo.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Atenção:</strong> Isso não salva, não sincroniza o repositório e não aplica nada automaticamente. Você deve revisar o JSON e clicar em "Salvar e Aplicar" se quiser enviá-lo para sua loja.
            </p>
            <div className="flex space-x-3 mt-2">
              <button
                onClick={() => setShowSyncWakeModal(false)}
                disabled={isSyncWakeLoading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleSyncWake}
                disabled={isSyncWakeLoading}
                className="flex-1 px-4 py-2 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isSyncWakeLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sincronizando...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Confirmar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação - Sync settings.json */}
      {showSyncSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-md max-w-md w-full mx-4 p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar Sync settings.json</h3>
            <p className="text-sm text-gray-700 mb-4">
              Isso vai buscar os dados diretamente do arquivo local <code>Configs/settings.json</code> da loja selecionada e exibí-los no editor.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Atenção:</strong> Nenhuma alteração de git será feita e seu repositório não será sobrescrito ou reiniciado por esta ação. Você só perderá as edições não salvas que já estiverem no editor.
            </p>
            <div className="flex space-x-3 mt-2">
              <button
                onClick={() => setShowSyncSettingsModal(false)}
                disabled={isSyncSettingsLoading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleSyncSettings}
                disabled={isSyncSettingsLoading}
                className="flex-1 px-4 py-2 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isSyncSettingsLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sincronizando...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Confirmar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}

